import { Button, Modal } from '@safetyhub/ui';

type NodePasteChoiceModalProps = {
  /** 모달 열림 여부. false면 렌더 안 함. */
  open: boolean;
  /**
   * '바인딩·데이터 함께' 선택 시 대상 템플릿에 새로 이식될 의존 소스 key 목록.
   * 비어 있으면(대상에 이미 모두 있음) "함께" 버튼 안내를 생략한다.
   */
  addedDependencyKeys: string[];
  /**
   * 복사 노드에 결재란(approvalBlock)이 포함돼 템플릿 레벨 결재 메타가 함께 실렸는지 여부.
   * true면 '함께' 옵션 안내에 "결재 설정 포함"을 덧붙인다(대상에 기존 결재 설정 없을 때만 적용).
   */
  hasApprovalMeta?: boolean;
  /** 선택 — 노드만 삽입(nodes-only) 또는 의존 소스까지 함께(with-deps). */
  onChoose: (mode: 'nodes-only' | 'with-deps') => void;
  onCancel: () => void;
};

/**
 * 노드 붙여넣기 선택 모달 — 출처와 다른 템플릿에 붙여넣을 때 의존 소스 이식 여부를 묻는다.
 * 오버레이·z-index(z-[70])·Esc/오버레이 닫기·포커스 트랩은 공용 [[Modal]]이 소유한다.
 * - '노드만': 노드 트리만 삽입(바인딩 문자열은 유지 — 대상에 같은 key 소스가 있으면 자동 연결, 없으면 미해결).
 * - '바인딩·데이터 함께': 노드가 참조하는 템플릿 스코프 dataDefs/dataSources를 대상에 이식 후 삽입.
 *   대상에 없던 소스 key가 있으면 그 목록을 안내해 운영자가 이식 범위를 인지하게 한다.
 * - 오버레이/Esc/취소=onCancel. 선택지가 셋(취소/노드만/함께)이라 default 액션은 두지 않는다.
 */
export function NodePasteChoiceModal({
  open,
  addedDependencyKeys,
  hasApprovalMeta = false,
  onChoose,
  onCancel,
}: NodePasteChoiceModalProps) {
  const hasAdded = addedDependencyKeys.length > 0;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="노드 붙여넣기"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            취소
          </Button>
          <Button variant="secondary" onClick={() => onChoose('nodes-only')}>
            노드만
          </Button>
          <Button variant="primary" onClick={() => onChoose('with-deps')}>
            바인딩·데이터 함께
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-gray-600">
        다른 템플릿에서 복사한 노드입니다. 노드가 참조하는 데이터 소스(바인딩)를 함께 가져올지
        선택하세요.
      </p>
      {hasAdded && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-xs font-medium text-amber-800">
            '함께' 선택 시 이식되는 데이터 소스 ({addedDependencyKeys.length}개):
          </p>
          <p className="mt-1 break-all text-xs leading-relaxed text-amber-700">
            {addedDependencyKeys.join(', ')}
          </p>
        </div>
      )}
      {!hasAdded && (
        <p className="mt-3 text-xs leading-relaxed text-gray-500">
          참조하는 데이터 소스가 없거나 대상 템플릿에 이미 모두 존재합니다.
        </p>
      )}
      {hasApprovalMeta && (
        <p className="mt-3 text-xs leading-relaxed text-brand-700">
          '함께' 선택 시 결재 설정(결재라인·배치)도 포함됩니다. 단 대상 템플릿에 기존 결재 설정이
          없을 때만 적용됩니다.
        </p>
      )}
    </Modal>
  );
}
